import { graphql, readInlineData } from 'react-relay/legacy';
import braintreeClient from 'braintree-web/client';
import braintreeApplePay from 'braintree-web/apple-pay';
import { getGuestUserId } from '../../../helpers/checkoutGuestUserToken';
import CheckoutMutationWrapper from '../../../mutations/CheckoutMutationWrapper';
import { shouldVerifyPaymentMethod } from '../../../helpers/checkoutRedirects';
import { logCheckoutError } from '../../../helpers/logCheckoutError';
import { executeCaptcha } from 'dibs-recaptcha/exports/recaptcha';
import { setProcessingErrorAction } from '../../../actions/commonActions';

export const setUpApplePay = async ({
    transaction: transactionRef,
    viewer: viewerRef,
    setApplePayPaymentRequest,
    setIsProcessingApplePay,
    setApplePayClientInstance,
    dispatch,
}) => {
    try {
        //get graphql data
        const transactionData = readInlineData(
            graphql`
                fragment applePayCheckout_transaction on Transaction @inline {
                    buyerChargeCurrency
                    buyerInvoice {
                        totalAmount {
                            convertedAmountList {
                                currency
                                amount
                            }
                        }
                    }
                }
            `,
            transactionRef
        );

        const viewerData = readInlineData(
            graphql`
                fragment applePayCheckout_viewer on Viewer @inline {
                    clientToken {
                        value
                    }
                }
            `,
            viewerRef
        );

        setApplePayPaymentRequest(null);
        setIsProcessingApplePay(true);

        //apple pay payment data
        const buyerChargeCurrencyFromTransaction = transactionData?.buyerChargeCurrency;
        const authorizationAmountObject =
            transactionData?.buyerInvoice?.totalAmount?.convertedAmountList?.find(
                amount => amount.currency === buyerChargeCurrencyFromTransaction
            ) || {};
        const authorizationAmount = authorizationAmountObject.amount;

        //braintree and Apple Pay interactions
        const clientInstance = await braintreeClient.create({
            authorization: viewerData?.clientToken?.value,
        });
        const applePayInstance = await braintreeApplePay.create({
            client: clientInstance,
        });
        const paymentRequest = await applePayInstance.createPaymentRequest({
            total: {
                label: '1stDibs',
                amount: authorizationAmount,
            },
            currencyCode: buyerChargeCurrencyFromTransaction,
        });
        setIsProcessingApplePay(false);
        setApplePayClientInstance(applePayInstance);
        return setApplePayPaymentRequest(paymentRequest || null);
    } catch (error) {
        logCheckoutError(error, 'applepay');
        dispatch(
            setProcessingErrorAction({
                errorStatus: 'APPLE_PAY',
                errorMessage: 'Apple Pay Error',
            })
        );
        dispatch({
            type: 'TRANSACTION_SUBMIT_ERROR',
            errorStatus: 'APPLE_PAY',
            errorMessage: 'Apple Pay Error',
        });
        return setIsProcessingApplePay(false);
    }
};

export const performApplePayAuthorization = async ({
    transactionState,
    relay,
    buyerId,
    applePayClientInstance,
    setIsProcessingApplePay,
    applePayPaymentRequest,
    onApplePayTokenSaved,
    callSubmit,
    isMobile,
    dispatch,
}) => {
    try {
        setIsProcessingApplePay(true);
        const guestUserId = getGuestUserId();
        const applePaySession = new window.ApplePaySession(3, applePayPaymentRequest);
        applePaySession.onpaymentauthorized = event => {
            applePayClientInstance.tokenize(
                { token: event.payment.token },
                async function (tokenizationError, tokenizedPayload) {
                    if (tokenizationError) {
                        logCheckoutError(tokenizationError, 'applepay');
                        applePaySession.completePayment(window.ApplePaySession.STATUS_FAILURE);
                        return null;
                    }
                    let applePayToken;
                    try {
                        await applePaySession.completePayment(
                            window.ApplePaySession.STATUS_SUCCESS
                        );

                        const captchaResults = await executeCaptcha({
                            action: 'checkoutVaultApplePay',
                            featureFlag: 'vaultCreditCardCaptcha',
                            featureFlagV3: 'vaultCreditCardCaptchaV3',
                            relayEnvironment: relay.environment,
                        });
                        const vaultResponse = await CheckoutMutationWrapper.commit(
                            'UserVaultPaymentMethodMutation',
                            relay.environment,
                            {
                                userId: buyerId,
                                saveToWallet: !guestUserId,
                                paymentMethodNonce: tokenizedPayload?.nonce,
                                verifyPaymentMethod: shouldVerifyPaymentMethod(),
                                captchaToken: captchaResults?.captchaToken,
                                captchaKey: captchaResults?.captchaKey,
                            },
                            isMobile
                        );

                        applePayToken =
                            vaultResponse.userVaultPaymentMethod?.walletPaymentMethod?.paymentMethod
                                ?.token;

                        onApplePayTokenSaved(applePayToken);
                    } catch (error) {
                        logCheckoutError(error, 'applepay');
                        dispatch(
                            setProcessingErrorAction({
                                errorStatus: 'APPLE_PAY',
                                errorMessage: 'Apple Pay Error',
                            })
                        );
                        dispatch({
                            type: 'TRANSACTION_SUBMIT_ERROR',
                            errorStatus: 'APPLE_PAY',
                            errorMessage: 'Apple Pay Error',
                        });
                        setIsProcessingApplePay(false);
                        return null;
                    }
                    // send state with token
                    const transactionStateWithToken = {
                        ...transactionState,
                        paymentMethodToken: applePayToken,
                    };

                    return callSubmit(transactionStateWithToken);
                }
            );
        };
        applePaySession.onvalidatemerchant = event => {
            applePayClientInstance.performValidation(
                {
                    validationURL: event.validationURL,
                    displayName: '1stDibs',
                },
                function (validateMerchantError, merchantSession) {
                    if (validateMerchantError) {
                        logCheckoutError(validateMerchantError, 'applepay');
                        return;
                    }
                    applePaySession.completeMerchantValidation(merchantSession);
                }
            );
        };
        applePaySession.oncancel = () => {
            setIsProcessingApplePay(false);
        };
        applePaySession.begin();
    } catch (error) {
        logCheckoutError(error, 'applepay');
        dispatch(
            setProcessingErrorAction({
                errorStatus: 'APPLE_PAY',
                errorMessage: 'Apple Pay Error',
            })
        );
        dispatch({
            type: 'TRANSACTION_SUBMIT_ERROR',
            errorStatus: 'APPLE_PAY',
            errorMessage: 'Apple Pay Error',
        });
        setIsProcessingApplePay(false);
    }
};
